import type { Gym, Org, OrgMember, User, UserCredentials } from "@api/types";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useEffect, useState } from "react";

type UseAuthState = {
  user: User | null;
  credentials: UserCredentials | null;
  gym: Gym | null;
  org: Org | null;
  tempPhoneNumber: string | null;
  orgs: OrgMember[];
  setUser: (u: User) => void;
  setCredentials: (c: UserCredentials) => void;
  setGym: (gym: Gym | null) => void;
  setOrg: (org: Org) => void;
  setTempPhoneNumber: (tempPhoneNumber: string | null) => void;
  clear: () => void;
};

/**
 * see https://github.com/pmndrs/zustand/wiki/Persisting-the-store's-data#persist-middleware for docs on
 * zustand persist middleware
 * */

export const useAuthStore = create<UseAuthState>()(
  persist(
    (set, _get) => ({
      user: null,
      credentials: null,
      gym: null,
      org: null,
      tempPhoneNumber: null,
      orgs: [],
      setUser: (user: User) => set({ user }),
      setCredentials: (credentials: UserCredentials) => set({ credentials }),
      setGym: (gym: Gym | null) => set({ gym }),
      setOrg: (org: Org) => set({ org }),
      setTempPhoneNumber: (tempPhoneNumber: string | null) => set({ tempPhoneNumber }),
      clear: () => set({ user: null, credentials: null, gym: null, org: null, orgs: [] }),
    }),
    {
      name: "helios.auth",
      version: 4,
    },
  ),
);

export function useBarre3() {
  return useAuthStore((state) => state?.user?.email.includes("barre3.com"));
}

export function useAuthUser() {
  return useAuthStore((state) => state.user);
}

/** True for Helios staff accounts; QA-only affordances are limited to them. */
export function useInternalUser(): boolean {
  return useAuthStore(
    (state) => state.user?.email?.toLowerCase().endsWith("@sendhelios.com") ?? false,
  );
}

/**
 * True once the persisted auth store has rehydrated from localStorage.
 * AuthGate must wait for this before treating `user === null` as logged
 * out — otherwise a fresh navigation to /messages after login can redirect
 * back to /login while rehydration is still in flight.
 */
export function useAuthStoreHydrated(): boolean {
  // Start `false` so the first client render matches the server (which has
  // no localStorage and renders the loader). Flipping this in an effect
  // avoids a hydration mismatch while still gating the redirect on real
  // rehydration. localStorage is synchronous, so for most loads the store
  // is already hydrated and this resolves on the very next render.
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (useAuthStore.persist.hasHydrated()) {
      setHydrated(true);
      return;
    }
    return useAuthStore.persist.onFinishHydration(() => {
      setHydrated(true);
    });
  }, []);

  return hydrated;
}

export function useUserOrgs() {
  return useAuthStore((state) => state.orgs);
}

export function usePersistedGym(): [Gym | null, (gym: Gym | null) => void] {
  return useAuthStore((state) => [state.gym, state.setGym]);
}

export function usePersistedOrg(): [Org | null, (org: Org) => void] {
  return useAuthStore((state) => [state.org, state.setOrg]);
}

export function usePersistedPhoneNumber(): [
  string | null,
  (tempPhoneNumber: string | null) => void,
] {
  return useAuthStore((state) => [state.tempPhoneNumber, state.setTempPhoneNumber]);
}
